import { getPersonData } from "../../utils/myinfoData";
import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { message, Modal, Spin } from "antd";
import ProfileViewer from "../../pages/borrower-dashboard/ProfileViewer";

const cardTypeStyles = {
  corporate: {
    label: "Corporate Loan",
    badgeBg: "#EAF3FF",
    badgeColor: "#0E5AA5",
    sortOrder: 1,
  },
  personal: {
    label: "Personal Loan",
    badgeBg: "#FFF4E9",
    badgeColor: "#B26700",
    sortOrder: 2,
  },
  property: {
    label: "Property Term Loan",
    badgeBg: "#EAF9F2",
    badgeColor: "#1B7F4B",
    sortOrder: 3,
  },
};

const sectionTitleStyle = {
  fontFamily: "Poppins, sans-serif",
  fontWeight: 700,
  fontSize: "20px",
  color: "#142C44",
  marginBottom: "12px",
};

const subTextStyle = {
  fontFamily: "Manrope, sans-serif",
  fontWeight: 600,
  fontSize: "12px",
  color: "rgba(0,0,0,0.45)",
  margin: 0,
};

const valueTextStyle = {
  fontFamily: "Manrope, sans-serif",
  fontWeight: 800,
  fontSize: "16px",
  color: "rgba(0,0,0,0.65)",
  margin: "2px 0 12px 0",
};

const controlSelectStyle = {
  borderRadius: "10px",
  border: "1px solid rgba(14, 90, 165, 0.2)",
  height: "36px",
  padding: "0 12px",
  fontFamily: "Manrope, sans-serif",
  fontSize: "13px",
  color: "#142C44",
  backgroundColor: "#fff",
};

const parseJsonSafe = (value, fallback = null) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const getPersonalLoanMyInfoFullName = (loan) => {
  const possibleMyInfoPayloads = [
    loan?.myinfo_data,
    loan?.borrower?.myinfo_data,
    loan?.borrower_summary?.myinfo_data,
    loan?.borrower_data?.myinfo_data,
  ];

  for (const payload of possibleMyInfoPayloads) {
    const parsed = parseJsonSafe(payload);
    const fullName =
      parsed?.name?.value ||
      getPersonData(parsed)?.name?.value ||
      parsed?.entity?.["basic-profile"]?.["entity-name"]?.value;

    if (fullName) {
      return fullName;
    }
  }

  return null;
};

const getPersonalLoanName = (loan) =>
  getPersonalLoanMyInfoFullName(loan) ||
  loan?.borrower_summary?.name ||
  loan?.borrower?.entity_name ||
  loan?.borrower?.principal_name ||
  loan?.principal_name ||
  "Borrower";

const formatCurrency = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 0,
  });
};

const formatRate = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return `${(num * 100).toFixed(1)}%`;
};

const formatDate = (dateString) => {
  if (!dateString) return "-";

  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true, // <-- forces AM/PM
  });
};

const getTimestamp = (dateString) => {
  if (!dateString) return 0;
  const d = new Date(dateString);
  const timestamp = d.getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const isOlderThanThirtyDays = (dateString) => {
  const createdAtTimestamp = getTimestamp(dateString);
  if (!createdAtTimestamp) return false;

  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
  return Date.now() - createdAtTimestamp > thirtyDaysMs;
};

const isWithinLastThirtyDays = (dateString) => {
  if (!dateString) return false;
  const createdAtDate = new Date(dateString);
  if (Number.isNaN(createdAtDate.getTime())) return false;

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return createdAtDate >= thirtyDaysAgo;
};

const normalizeCorporateLoan = (loan) => {
  const rawLoanAuthorizations = Array.isArray(loan?.loan_authorizations)
    ? loan.loan_authorizations
    : parseJsonSafe(loan?.loan_authorizations, []);

  return {
    ...loan,
    singpass_data: parseJsonSafe(loan?.singpass_data, loan?.singpass_data),
    credit_report_data: parseJsonSafe(
      loan?.credit_report_data,
      loan?.credit_report_data,
    ),
    loan_authorizations: rawLoanAuthorizations
      .filter((authorization) => isWithinLastThirtyDays(authorization?.created_at))
      .map((authorization) => ({
        ...authorization,
        credit_report_data: parseJsonSafe(
          authorization?.credit_report_data,
          authorization?.credit_report_data,
        ),
      })),
  };
};

const getFirstLoanDetailRecord = (payload) => {
  const details = payload?.["Loan Detail"];
  if (Array.isArray(details)) return details[0] || null;
  return details || null;
};

const renderCard = ({
  key,
  id,
  borrowerName,
  type,
  amount,
  rate,
  tenor,
  appliedOn,
  expiredOn,
  isStale,
  blankoContact,
  onBorrowerClick,
  onDownloadClick,
  onToggleBlankoContact,
  onOverwriteAuthentication,
  onEditLoanRemark,
  onEditBorrowerNotes,
  authorizationRequired,
  isDownloading,
}) => {
  const style = cardTypeStyles[type] || cardTypeStyles.corporate;

  return (
    <div
      key={key}
      style={{
        background: isStale ? "rgba(162, 162, 162, 0.95)" : "#ffffffb3",
        borderRadius: "12px",
        padding: "16px",
        minHeight: "295px",
        border: isStale
          ? "1px solid rgba(0,0,0,0.12)"
          : "1px solid rgba(14,90,165,0.08)",
        filter: isStale ? "grayscale(0.35)" : "none",
        opacity: isStale ? 0.78 : 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <button
            onClick={onBorrowerClick}
            style={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 700,
              fontSize: "16px",
              color: "#142C44CC",
              margin: 0,
              border: "none",
              background: "transparent",
              padding: 0,
              textAlign: "left",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {borrowerName || "-"}
          </button>
          <span
            style={{
              background: style.badgeBg,
              color: style.badgeColor,
              borderRadius: "999px",
              padding: "4px 10px",
              fontFamily: "Manrope, sans-serif",
              fontWeight: 700,
              fontSize: "12px",
              whiteSpace: "nowrap",
              height: "fit-content",
            }}
          >
            {style.label}
          </span>
        </div>

        {isStale ? (
          <p
            style={{
              ...subTextStyle,
              color: "rgba(0,0,0,0.6)",
              fontWeight: 700,
              marginBottom: "8px",
            }}
          >
            Older than 30 days
          </p>
        ) : null}

        <p style={subTextStyle}>Loan ID</p>
        <p style={valueTextStyle}>{id || "-"}</p>

        <p style={subTextStyle}>Requested Amount</p>
        <p style={valueTextStyle}>{formatCurrency(amount)}</p>

        <p style={subTextStyle}>Preferred Rate</p>
        <p style={valueTextStyle}>
          {formatRate(style.label === "Personal Loan" ? rate / 100 : rate)}
        </p>

        <p style={subTextStyle}>Preferred Tenor</p>
        <p style={valueTextStyle}>{tenor ? `${tenor} Months` : "-"}</p>

        <p style={subTextStyle}>Applied On</p>
        <p style={valueTextStyle}>{formatDate(appliedOn)}</p>

        <p style={subTextStyle}>Expired On</p>
        <p style={{ ...valueTextStyle, marginBottom: 0 }}>
          {formatDate(expiredOn)}
        </p>
      </div>
      <div style={{ width: "100%" }}>
        {type === "corporate" || type === "personal" ? (
          <>
            {type === "corporate" && authorizationRequired ? (
              <button
                onClick={onOverwriteAuthentication}
                style={{
                  marginTop: "14px",
                  width: "100%",
                  border: "2px solid rgba(14, 90, 165, 0.2)",
                  borderRadius: "14px",
                  padding: "6px 10px",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#0e5aa5",
                  background: "#f5fbff",
                  cursor: "pointer",
                }}
              >
                Overwrite Authentication
              </button>
            ) : null}
            <button
              onClick={onEditLoanRemark}
              style={{
                marginTop: "14px",
                width: "100%",
                border: "2px solid rgba(14, 90, 165, 0.2)",
                borderRadius: "14px",
                padding: "6px 10px",
                fontFamily: "Manrope, sans-serif",
                fontSize: "12px",
                fontWeight: 700,
                color: "#0e5aa5",
                background: "#f5fbff",
                cursor: "pointer",
              }}
            >
              Edit Loan Remark
            </button>
            <button
              onClick={onEditBorrowerNotes}
              style={{
                marginTop: "14px",
                width: "100%",
                border: "2px solid rgba(14, 90, 165, 0.2)",
                borderRadius: "14px",
                padding: "6px 10px",
                fontFamily: "Manrope, sans-serif",
                fontSize: "12px",
                fontWeight: 700,
                color: "#0e5aa5",
                background: "#f5fbff",
                cursor: "pointer",
              }}
            >
              Edit Borrower Notes
            </button>
          </>
        ) : null}
        <button
          onClick={onToggleBlankoContact}
          style={{
            marginTop: "14px",
            width: "100%",
            border: blankoContact
              ? "2px solid rgba(176, 79, 35, 0.35)"
              : "2px solid rgba(14, 90, 165, 0.2)",
            borderRadius: "14px",
            padding: "6px 10px",
            fontFamily: "Manrope, sans-serif",
            fontSize: "12px",
            fontWeight: 700,
            color: blankoContact ? "#9a4b1f" : "#0e5aa5",
            background: blankoContact ? "#fff4ed" : "#f5fbff",
            cursor: "pointer",
          }}
        >
          Blanko Contact: {blankoContact ? "On" : "Off"}
        </button>
        <button
          onClick={onDownloadClick}
          disabled={isDownloading}
          style={{
            marginTop: "14px",
            width: "100%",
            border: "2px solid lightgrey",
            borderRadius: "14px",
            padding: "6px 10px",
            fontFamily: "Manrope, sans-serif",
            fontSize: "12px",
            fontWeight: 700,
            color: "#6c6c6c",
            background: "transparent",
            cursor: isDownloading ? "not-allowed" : "pointer",
            opacity: isDownloading ? 0.6 : 1,
          }}
        >
          {isDownloading
            ? "Preparing download..."
            : "Download Borrower Documents"}
        </button>
      </div>
    </div>
  );
};

const AllLoans = () => {
  const [loading, setLoading] = useState(true);
  const [corporateLoans, setCorporateLoans] = useState([]);
  const [personalLoans, setPersonalLoans] = useState([]);
  const [propertyLoans, setPropertyLoans] = useState([]);
  const [sortBy, setSortBy] = useState("createdDate");
  const [downloadingLoanKey, setDownloadingLoanKey] = useState(null);

  const [dataSingpassModal, setDataSingpassModal] = useState(false);
  const [myinfoData, setMyinfoData] = useState(null);
  const [otherAuthoriserDetails, setOtherAuthoriserDetails] = useState(null);
  const [viewingUser, setViewingUser] = useState(0);
  const [viewingTab, setViewingTab] = useState(0);
  const [liveUserData, setLiveUserData] = useState(null);
  const [singpassData, setSingpassData] = useState(null);
  const [combinedSingpassData, setCombinedSingpassData] = useState([]);
  const [badgeVerified, setBadgeVerified] = useState(false);
  const [backupContact, setBackupContact] = useState("");
  const [isPersonalLoanProfile, setIsPersonalLoanProfile] = useState(false);
  const [selectedLoanDocuments, setSelectedLoanDocuments] = useState(null);
  const [sccbReportData, setSccbReportData] = useState(null);
  const [notes, setNotes] = useState("");
  const [platformNotes, setPlatformNotes] = useState("");
  const [loanRemark, setLoanRemark] = useState("");
  const [selectedCorporateLoan, setSelectedCorporateLoan] = useState(null);
  const [editLoanRemarkModalIsOpen, setEditLoanRemarkModalIsOpen] =
    useState(false);
  const [editBorrowerNotesModalIsOpen, setEditBorrowerNotesModalIsOpen] =
    useState(false);
  const [remarkDetails, setRemarkDetails] = useState(null);
  const [borrowerNotes, setBorrowerNotes] = useState("");
  const [keyman, setKeyman] = useState("");
  const [allCBSData, setAllCBSData] = useState([]);
  const [bankAnalysisContext, setBankAnalysisContext] = useState({
    loanId: null,
    loanType: null,
    enabled: false,
  });
  const [mlcbAnalysisContext, setMlcbAnalysisContext] = useState({
    loanId: null,
    enabled: false,
  });
  const [hideContactInfo, setHideContactInfo] = useState(false);

  const baseAvailableTab = useMemo(
    () => [
      { name: "Personal Information" },
      { name: "Corporate Information" },
      { name: "NOA History" },
      { name: "Capitals" },
      { name: "Financials" },
      { name: "Shareholders" },
      { name: "Borrower Report" },
      { name: "Customer Document Analysis", isNew: true },
      { name: "MLCB Analysis", isNew: true },
    ],
    [],
  );

  const availableTab = useMemo(() => {
    const tabs = [...baseAvailableTab];
    if (sccbReportData) {
      const insertIndex = tabs.findIndex(
        (tab) => tab.name === "Borrower Report",
      );
      const sccbTab = { name: "SCCB Report", isNew: true };
      if (insertIndex >= 0) {
        tabs.splice(insertIndex + 1, 0, sccbTab);
      } else {
        tabs.push(sccbTab);
      }
    }
    return tabs;
  }, [baseAvailableTab, sccbReportData]);

  const resetProfileViewer = () => {
    setDataSingpassModal(false);
    setSingpassData(null);
    setMyinfoData(null);
    setLiveUserData(null);
    setOtherAuthoriserDetails(null);
    setCombinedSingpassData([]);
    setViewingTab(0);
    setViewingUser(0);
    setBadgeVerified(false);
    setBackupContact("");
    setIsPersonalLoanProfile(false);
    setSelectedLoanDocuments(null);
    setSccbReportData(null);
    setNotes("");
    setPlatformNotes("");
    setLoanRemark("");
    setSelectedCorporateLoan(null);
    setEditLoanRemarkModalIsOpen(false);
    setEditBorrowerNotesModalIsOpen(false);
    setRemarkDetails(null);
    setBorrowerNotes("");
    setKeyman("");
    setAllCBSData([]);
    setBankAnalysisContext({ loanId: null, loanType: null, enabled: false });
    setMlcbAnalysisContext({ loanId: null, enabled: false });
    setHideContactInfo(false);
  };

  const singpassDetails = async ({
    userId,
    otherAuthorization = false,
    loanId = null,
  }) => {
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/user/getBorrowerSingppass?user_id=${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Unable to load borrower profile.");
      }

      const json = await response.json();
      const primarySingpass = parseJsonSafe(
        json.singpass_data,
        json.singpass_data,
      );
      const allSingpassData = primarySingpass ? [primarySingpass] : [];

      setDataSingpassModal(true);
      setMyinfoData(json.myinfo_data || null);
      setLiveUserData(primarySingpass || null);

      if (otherAuthorization && loanId) {
        try {
          const response2 = await fetch(
            "https://www.smart-lend.com/api/loanDetails/getLoanAuthorisers",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Cookies.get("token")}`,
              },
              body: JSON.stringify({ loan_id: loanId }),
            },
          );

          if (response2.ok) {
            const json2 = await response2.json();
            const authorisers = json2?.authorisers || [];
            setOtherAuthoriserDetails(authorisers);
            authorisers.forEach((authoriser) => {
              const parsed = parseJsonSafe(authoriser?.singpass_data);
              if (parsed) allSingpassData.push(parsed);
            });
          }
        } catch (error) {
          console.error(error);
          setOtherAuthoriserDetails(null);
        }
      } else {
        setOtherAuthoriserDetails(null);
      }

      setCombinedSingpassData(allSingpassData);
      setSingpassData(primarySingpass || null);
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Unable to load borrower profile.");
    }
  };

  const fetchCorporateLoanDetail = async (loanId) => {
    if (!loanId) return null;

    const response = await fetch(
      `https://www.smart-lend.com/api/loan/loanRequest/viewLoanRequestDetail?loan_id=${loanId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error("Unable to load full corporate loan detail.");
    }

    const json = await response.json();
    const loanDetail = getFirstLoanDetailRecord(json);
    return loanDetail ? normalizeCorporateLoan(loanDetail) : null;
  };

  const openCorporateLoanProfile = async (loanItem) => {
    const rawLoan = loanItem?.rawLoan;
    if (!loanItem?.borrowerUserId || !rawLoan) return;

    let resolvedLoan = rawLoan;
    try {
      const detailedLoan = await fetchCorporateLoanDetail(rawLoan?.loan_id);
      if (detailedLoan) {
        resolvedLoan = detailedLoan;
      }
    } catch (error) {
      console.error(
        "[AllLoans] Failed to fetch detailed corporate loan before opening profile viewer.",
        error,
      );
    }

    setIsPersonalLoanProfile(false);
    setViewingTab(0);
    setViewingUser(0);
    setBackupContact(loanItem.borrowerUserId);
    setBadgeVerified(Boolean(resolvedLoan?.badge_verified));
    setSccbReportData(null);
    setSelectedLoanDocuments(null);
    setBankAnalysisContext({
      loanId: resolvedLoan?.loan_id || null,
      loanType: "corporate",
      enabled: Boolean(resolvedLoan?.precheck),
    });
    setMlcbAnalysisContext({
      loanId: resolvedLoan?.loan_id || null,
      enabled: false,
    });
    setKeyman(resolvedLoan?.keyman || "");
    setNotes(resolvedLoan?.notes || "");
    setPlatformNotes(resolvedLoan?.platform_notes || "");
    setLoanRemark(resolvedLoan?.loan_remark || "");
    setSelectedCorporateLoan(resolvedLoan);
    setHideContactInfo(Boolean(resolvedLoan?.blanko_contact));

    const baseCbs = [];
    const borrowerSingpass = parseJsonSafe(resolvedLoan?.singpass_data, {});
    const normalizedLoanAuthorizations = Array.isArray(
      resolvedLoan?.loan_authorizations,
    )
      ? resolvedLoan.loan_authorizations
      : parseJsonSafe(resolvedLoan?.loan_authorizations, []);
    baseCbs.push({
      user_id: resolvedLoan?.user_id,
      name:
        getPersonData(borrowerSingpass)?.name?.value || loanItem?.borrowerName || "",
      cbs_data: parseJsonSafe(
        resolvedLoan?.credit_report_data,
        resolvedLoan?.credit_report_data,
      ),
    });

    normalizedLoanAuthorizations.forEach((authorization) => {
      baseCbs.push({
        user_id: authorization?.user_id,
        name: authorization?.director_name || "Director",
        cbs_data: parseJsonSafe(
          authorization?.credit_report_data,
          authorization?.credit_report_data,
        ),
      });
    });

    console.log("[AllLoans][Corporate CBS] Prepared CBS entries", {
      loanId: resolvedLoan?.loan_id || rawLoan?.loan_id || null,
      source: resolvedLoan === rawLoan ? "list_payload" : "detail_payload",
      totalEntriesBeforeFilter: baseCbs.length,
      entriesWithCbsData: baseCbs.filter((entry) => entry?.cbs_data).map(
        (entry) => ({
          user_id: entry?.user_id ?? null,
          name: entry?.name ?? null,
        }),
      ),
    });

    setAllCBSData(baseCbs.filter((entry) => entry?.cbs_data));

    await singpassDetails({
      userId: loanItem.borrowerUserId,
      otherAuthorization: Boolean(resolvedLoan?.authorization_required),
      loanId: resolvedLoan?.loan_id,
    });
  };

  const openPersonalLoanProfile = async (loanItem) => {
    const rawLoan = loanItem?.rawLoan;
    if (!loanItem?.borrowerUserId || !rawLoan) return;

    setIsPersonalLoanProfile(true);
    setViewingTab(0);
    setViewingUser(0);
    setBackupContact(loanItem.borrowerUserId);
    setBadgeVerified(false);
    setSccbReportData(null);
    setSelectedLoanDocuments(rawLoan);
    setBankAnalysisContext({
      loanId: rawLoan?.id || null,
      loanType: "personal",
      enabled: Boolean(rawLoan?.precheck),
    });
    setMlcbAnalysisContext({
      loanId: rawLoan?.id || null,
      enabled: Boolean(
        rawLoan?.allow_lender_visibility && rawLoan?.mlcb_report_document,
      ),
    });
    setKeyman("");
    setNotes("");
    setPlatformNotes(rawLoan?.platform_notes || "");
    setLoanRemark(rawLoan?.loan_remark || "");
    setSelectedCorporateLoan(null);
    setHideContactInfo(Boolean(rawLoan?.blanko_contact));

    setAllCBSData([]);

    await singpassDetails({ userId: loanItem.borrowerUserId });
  };

  const openPropertyLoanProfile = async (loanItem) => {
    const rawLoan = loanItem?.rawLoan;
    if (!loanItem?.borrowerUserId || !rawLoan) return;

    const reportPayload =
      rawLoan?.sccb_report?.response_payload ||
      rawLoan?.sccb_reports?.find((report) => report?.response_payload)
        ?.response_payload ||
      rawLoan?.sccbReports?.find((report) => report?.response_payload)
        ?.response_payload ||
      null;

    setIsPersonalLoanProfile(true);
    setViewingTab(0);
    setViewingUser(0);
    setBackupContact(loanItem.borrowerUserId);
    setBadgeVerified(false);
    setSccbReportData(reportPayload);
    setSelectedLoanDocuments(rawLoan);
    setBankAnalysisContext({
      loanId: rawLoan?.id || null,
      loanType: "property",
      enabled: false,
    });
    setMlcbAnalysisContext({ loanId: rawLoan?.id || null, enabled: false });
    setKeyman("");
    setNotes("");
    setPlatformNotes("");
    setLoanRemark("");
    setSelectedCorporateLoan(null);
    setHideContactInfo(Boolean(rawLoan?.blanko_contact));

    setAllCBSData([]);

    await singpassDetails({ userId: loanItem.borrowerUserId });
  };

  const handleBorrowerClick = async (loanItem) => {
    if (!loanItem) return;

    if (loanItem.type === "corporate") {
      await openCorporateLoanProfile(loanItem);
      return;
    }

    if (loanItem.type === "personal") {
      await openPersonalLoanProfile(loanItem);
      return;
    }

    if (loanItem.type === "property") {
      await openPropertyLoanProfile(loanItem);
    }
  };

  const openCorporateLoanActions = (loan) => {
    if (loan?.type !== "corporate" || !loan?.rawLoan) return;
    setSelectedCorporateLoan({
      ...loan.rawLoan,
      __adminLoanId: loan.rawLoan.loan_id,
      __loanType: "corporate",
    });
    setPlatformNotes(loan.rawLoan.platform_notes || "");
    setLoanRemark(loan.rawLoan.loan_remark || "");
  };

  const openLoanRemarkActions = (loan) => {
    if (!loan?.rawLoan) return;
    setSelectedCorporateLoan({
      ...loan.rawLoan,
      __adminLoanId:
        loan.type === "corporate" ? loan.rawLoan.loan_id : loan.rawLoan.id,
      __loanType: loan.type,
    });
    setPlatformNotes(loan.rawLoan.platform_notes || "");
    setLoanRemark(loan.rawLoan.loan_remark || "");
  };

  const updateLoanNotesState = (loanType, loanId, nextNotes) => {
    if (loanType === "personal") {
      setPersonalLoans((current) =>
        current.map((loan) =>
          String(loan.id) === String(loanId)
            ? { ...loan, platform_notes: nextNotes }
            : loan,
        ),
      );
      return;
    }

    setCorporateLoans((current) =>
      current.map((loan) =>
        String(loan.loan_id) === String(loanId)
          ? { ...loan, platform_notes: nextNotes }
          : loan,
      ),
    );
  };

  const updateLoanRemarkState = (loanType, loanId, serializedRemark) => {
    if (loanType === "personal") {
      setPersonalLoans((current) =>
        current.map((loan) =>
          String(loan.id) === String(loanId)
            ? { ...loan, loan_remark: serializedRemark }
            : loan,
        ),
      );
      return;
    }

    setCorporateLoans((current) =>
      current.map((loan) =>
        String(loan.loan_id) === String(loanId)
          ? { ...loan, loan_remark: serializedRemark }
          : loan,
      ),
    );
  };

  const editLoanNotes = async (loanId, nextNotes, loanType = "corporate") => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/loan/editLoanNotes",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            loan_id: loanId,
            loan_type: loanType,
            platform_notes: nextNotes,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Unable to update borrower notes.");
      }

      setPlatformNotes(nextNotes);
      setBorrowerNotes(nextNotes);
      setSelectedCorporateLoan((current) =>
        current ? { ...current, platform_notes: nextNotes } : current,
      );
      updateLoanNotesState(loanType, loanId, nextNotes);
      setEditBorrowerNotesModalIsOpen(false);
      message.success("Borrower notes updated!");
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Unable to update borrower notes.");
    }
  };

  const editLoanRemarks = async (
    loanId,
    nextRemarkDetails,
    loanType = "corporate",
  ) => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/admin/editLoanRemarks",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            loan_id: loanId,
            loan_type: loanType,
            loan_remark: JSON.stringify(nextRemarkDetails),
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Unable to update loan remark.");
      }

      const serializedRemark = JSON.stringify(nextRemarkDetails);
      setLoanRemark(serializedRemark);
      setRemarkDetails(nextRemarkDetails);
      setSelectedCorporateLoan((current) =>
        current ? { ...current, loan_remark: serializedRemark } : current,
      );
      updateLoanRemarkState(loanType, loanId, serializedRemark);
      setEditLoanRemarkModalIsOpen(false);
      message.success("Loan remark updated!");
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Unable to update loan remark.");
    }
  };

  const overwriteLoanAuthentications = async (loanId) => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/adminLoan/overWriteLoanAuthentication",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({ loan_id: loanId }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to overwrite authentication.");
      }

      setOtherAuthoriserDetails((current) =>
        Array.isArray(current)
          ? current.map((authoriser) => ({ ...authoriser, authorized: 1 }))
          : current,
      );
      setSelectedCorporateLoan((current) =>
        current
          ? {
              ...current,
              loan_authorizations: Array.isArray(current.loan_authorizations)
                ? current.loan_authorizations.map((authoriser) => ({
                    ...authoriser,
                    authorized: 1,
                  }))
                : current.loan_authorizations,
            }
          : current,
      );
      message.success("Loan authentication successfully overwritten!");
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Failed to overwrite authentication.");
    }
  };

  const downloadCorporateLoanDocuments = async (loanItem) => {
    const loanId = loanItem?.rawLoan?.loan_id || loanItem?.id;
    if (!loanId) return;

    const response = await fetch(
      "https://www.smart-lend.com/api/download-loan-documents",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: JSON.stringify({ loan_id: loanId }),
      },
    );

    const json = await response.json();
    if (!response.ok) {
      throw new Error(
        json?.message || "Unable to download corporate loan documents.",
      );
    }

    if (!json?.download_url) {
      throw new Error("Download link not available.");
    }

    window.open(json.download_url, "_blank", "noopener,noreferrer");
  };

  const downloadPersonalLoanDocuments = async (loanItem) => {
    const loanId = loanItem?.rawLoan?.id || loanItem?.id;
    if (!loanId) return;

    const response = await fetch(
      `https://www.smart-lend.com/api/personalLoans/${loanId}/documents/download`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
      },
    );

    const json = await response.json();
    if (!response.ok) {
      throw new Error(
        json?.message || "Unable to download personal loan documents.",
      );
    }

    if (!json?.download_url) {
      throw new Error("Download link not available.");
    }

    window.open(json.download_url, "_blank", "noopener,noreferrer");
  };

  const downloadPropertyLoanDocuments = async (loanItem) => {
    const loanId = loanItem?.rawLoan?.id || loanItem?.id;
    if (!loanId) return;

    const response = await fetch(
      `https://www.smart-lend.com/api/propTermLoan/${loanId}/documents/download`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
      },
    );

    const json = await response.json();
    if (!response.ok) {
      throw new Error(
        json?.message || "Unable to download property loan documents.",
      );
    }

    if (!json?.download_url) {
      throw new Error("Download link not available.");
    }

    window.open(json.download_url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadLoanDocuments = async (loanItem) => {
    if (!loanItem?.key) return;

    setDownloadingLoanKey(loanItem.key);
    try {
      if (loanItem.type === "corporate") {
        await downloadCorporateLoanDocuments(loanItem);
        return;
      }

      if (loanItem.type === "personal") {
        await downloadPersonalLoanDocuments(loanItem);
        return;
      }

      if (loanItem.type === "property") {
        await downloadPropertyLoanDocuments(loanItem);
      }
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Unable to download borrower documents.");
    } finally {
      setDownloadingLoanKey(null);
    }
  };

  const updateBlankoContactState = (loanType, loanId, enabled) => {
    if (loanType === "corporate") {
      setCorporateLoans((current) =>
        current.map((loan) =>
          String(loan.loan_id) === String(loanId)
            ? { ...loan, blanko_contact: enabled }
            : loan,
        ),
      );
      return;
    }

    if (loanType === "personal") {
      setPersonalLoans((current) =>
        current.map((loan) =>
          String(loan.id) === String(loanId)
            ? { ...loan, blanko_contact: enabled }
            : loan,
        ),
      );
      return;
    }

    setPropertyLoans((current) =>
      current.map((loan) =>
        String(loan.id) === String(loanId)
          ? { ...loan, blanko_contact: enabled }
          : loan,
      ),
    );
  };

  const toggleBlankoContact = async (loan) => {
    const nextValue = !loan?.rawLoan?.blanko_contact;

    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/admin/loans/blanko-contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            loan_type: loan.type,
            loan_id: loan.id,
            blanko_contact: nextValue,
          }),
        },
      );

      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to update Blanko Contact.");
      }

      updateBlankoContactState(loan.type, loan.id, json.blanko_contact);
      if (dataSingpassModal) {
        setHideContactInfo(Boolean(json.blanko_contact));
      }
      message.success(
        `Blanko Contact ${json.blanko_contact ? "enabled" : "disabled"}.`,
      );
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Unable to update Blanko Contact.");
    }
  };

  useEffect(() => {
    const fetchAllLoans = async () => {
      try {
        setLoading(true);
        const headers = {
          Authorization: `Bearer ${Cookies.get("token")}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        };

        const [corporateRes, personalRes, propertyRes] = await Promise.all([
          fetch("https://www.smart-lend.com/api/loan/viewLoanRequest", {
            method: "GET",
            headers,
          }),
          fetch(
            "https://www.smart-lend.com/api/personalLoans/admin/marketplace",
            {
              method: "GET",
              headers,
            },
          ),
          fetch(
            "https://www.smart-lend.com/api/propTermLoan/admin/getAllPropLoans",
            {
              method: "GET",
              headers,
            },
          ),
        ]);

        if (!corporateRes.ok || !personalRes.ok || !propertyRes.ok) {
          throw new Error("Unable to load all loan categories.");
        }

        const [corporateJson, personalJson, propertyJson] = await Promise.all([
          corporateRes.json(),
          personalRes.json(),
          propertyRes.json(),
        ]);

        setCorporateLoans(
          (corporateJson["Requested Loan"] || []).map(normalizeCorporateLoan),
        );
        setPersonalLoans(personalJson.data || []);
        setPropertyLoans(propertyJson.data || []);
      } catch (error) {
        console.error(error);
        message.error(error.message || "Failed to fetch loans.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllLoans();
  }, []);

  const allLoanItems = useMemo(() => {
    const corporateItems = corporateLoans.map((loan) => ({
      key: `corporate-${loan.loan_id}`,
      id: loan.loan_id,
      borrowerName: loan.principal_name,
      borrowerUserId: loan.user_id,
      type: "corporate",
      amount: loan.request_amount,
      rate: loan.request_interest_rate,
      tenor: loan.request_period,
      appliedOn: loan.created_at,
      expiredOn: loan.expired_date,
      createdAtTimestamp: getTimestamp(loan.created_at),
      isStale: isOlderThanThirtyDays(loan.created_at),
      rawLoan: loan,
    }));

    const personalItems = personalLoans.map((loan) => ({
      key: `personal-${loan.id}`,
      id: loan.id,
      borrowerName: getPersonalLoanName(loan),
      borrowerUserId: loan?.user_id || loan?.borrower?.user_id,
      type: "personal",
      amount: loan.amount,
      rate: loan.desired_rate_pa,
      tenor: loan.tenor_months,
      appliedOn: loan.created_at,
      expiredOn: loan.expires_at,
      createdAtTimestamp: getTimestamp(loan.created_at),
      isStale: isOlderThanThirtyDays(loan.created_at),
      rawLoan: loan,
    }));

    const propertyItems = propertyLoans.map((loan) => {
      const request = loan.prop_term_loan_request || {};
      const rateMin = Number(request.interestRateMin);
      const rateMax = Number(request.interestRateMax);
      const blendedRate =
        !Number.isNaN(rateMin) && !Number.isNaN(rateMax)
          ? (rateMin + rateMax) / 2 / 100
          : Number(request.interestRateMin) / 100;

      return {
        key: `property-${loan.id}`,
        id: loan.id,
        borrowerName:
          loan?.borrower_data?.entity_name ||
          loan?.borrower_data?.principal_name,
        borrowerUserId: loan?.user_id || loan?.borrower_data?.user_id,
        type: "property",
        amount: request.loanAmount,
        rate: blendedRate,
        tenor: request.loanTenure,
        appliedOn: loan.created_at,
        expiredOn: loan.expired_at,
        createdAtTimestamp: getTimestamp(loan.created_at),
        isStale: isOlderThanThirtyDays(loan.created_at),
        rawLoan: loan,
      };
    });

    return [...corporateItems, ...personalItems, ...propertyItems];
  }, [corporateLoans, personalLoans, propertyLoans]);

  const sortedLoanItems = useMemo(() => {
    const items = [...allLoanItems];

    if (sortBy === "loanType") {
      items.sort((a, b) => {
        const typeDiff =
          (cardTypeStyles[a.type]?.sortOrder || 999) -
          (cardTypeStyles[b.type]?.sortOrder || 999);

        if (typeDiff !== 0) {
          return typeDiff;
        }

        return b.createdAtTimestamp - a.createdAtTimestamp;
      });

      return items;
    }

    items.sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp);
    return items;
  }, [allLoanItems, sortBy]);

  const allCards = useMemo(
    () =>
      sortedLoanItems.map((loan) =>
        renderCard({
          ...loan,
          blankoContact: Boolean(loan?.rawLoan?.blanko_contact),
          onBorrowerClick: () => handleBorrowerClick(loan),
          onDownloadClick: () => handleDownloadLoanDocuments(loan),
          onToggleBlankoContact: () => toggleBlankoContact(loan),
          authorizationRequired: Boolean(loan?.rawLoan?.authorization_required),
          onOverwriteAuthentication: () => {
            openCorporateLoanActions(loan);
            overwriteLoanAuthentications(loan?.rawLoan?.loan_id);
          },
          onEditLoanRemark: () => {
            openLoanRemarkActions(loan);
            const loanId =
              loan.type === "corporate"
                ? loan?.rawLoan?.loan_id
                : loan?.rawLoan?.id;
            const existingRemarkDetails = loan?.rawLoan?.loan_remark
              ? parseJsonSafe(loan.rawLoan.loan_remark, {
                  type: "Good",
                  message: "",
                })
              : {
                  type: "Good",
                  message: "",
                };
            setRemarkDetails(
              {
                ...existingRemarkDetails,
                loan_id: loanId,
                loan_type: loan.type,
              },
            );
            setEditLoanRemarkModalIsOpen(true);
          },
          onEditBorrowerNotes: () => {
            openLoanRemarkActions(loan);
            setBorrowerNotes(loan?.rawLoan?.platform_notes || "");
            setEditBorrowerNotesModalIsOpen(true);
          },
          isDownloading: downloadingLoanKey === loan.key,
        }),
      ),
    [downloadingLoanKey, sortedLoanItems],
  );

  return (
    <div
      style={{
        margin: "20px",
        maxHeight: "calc(100vh - 130px)",
        overflowY: "auto",
      }}
    >
      <h2 style={sectionTitleStyle}>All Loan Requests (Admin)</h2>
      {loading ? (
        <div style={{ marginTop: "24px", textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: "14px",
            }}
          >
            <p
              style={{
                ...subTextStyle,
                fontSize: "13px",
              }}
            >
              Showing corporate, personal and property-backed loans.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#435669",
                }}
              >
                Sort by
              </span>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                style={controlSelectStyle}
              >
                <option value="createdDate">Created Date (Latest first)</option>
                <option value="loanType">Loan Type</option>
              </select>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: "14px",
              paddingBottom: "10px",
            }}
          >
            {allCards}
          </div>
        </>
      )}

      <Modal
        width={"60%"}
        style={{ backgroundColor: "rgba(0, 0, 0, 0)" }}
        open={dataSingpassModal}
        onCancel={resetProfileViewer}
        footer={null}
      >
        {singpassData || myinfoData ? (
          <ProfileViewer
            keyman={keyman}
            isMobile={false}
            viewingUser={viewingUser}
            setViewingUser={setViewingUser}
            singpassData={singpassData}
            liveUserData={liveUserData}
            myinfoData={myinfoData}
            otherAuthoriserDetails={otherAuthoriserDetails}
            setLiveUserData={setLiveUserData}
            setViewingTab={setViewingTab}
            viewingTab={viewingTab}
            badgeVerified={badgeVerified}
            availableTab={availableTab}
            allCBSData={allCBSData}
            combinedSingpassData={combinedSingpassData}
            getShareholderAddress={() => {}}
            backupContact={backupContact}
            notes={notes}
            platform_notes={platformNotes}
            loan_remark={loanRemark}
            personalOnly={isPersonalLoanProfile}
            loanDocuments={selectedLoanDocuments}
            hideContactInfo={hideContactInfo}
            bankAnalysisContext={bankAnalysisContext}
            mlcbAnalysisContext={mlcbAnalysisContext}
            sccbReportData={sccbReportData}
            sccbReportType="personal"
            sccbExcludeColumns={[
              "AMOUNT 2",
              "AMOUNT 3",
              "AMOUNT 4",
              "AMOUNT 5",
              "PLA STATUS",
              "PLA FILE REF",
              "DOC REMARKS",
              "STATUS DATE",
              "PLA STATUS DATE",
              "PLA ID",
              "DEF TYPE",
              "DEF STATUS",
              "DEF STATUS DATE",
              "DEF FILE REF",
              "DEF LAW FIRM",
              "DEF SOLICITOR",
              "DEF SOLICITOR ID",
              "PLA SOLICITOR ID",
              "CASE NO",
              "PAY LOAN ID",
            ]}
          />
        ) : (
          <div />
        )}
      </Modal>

      <Modal
        width={"60%"}
        style={{ backgroundColor: "rgba(0, 0, 0, 0)" }}
        open={editLoanRemarkModalIsOpen}
        onCancel={() => setEditLoanRemarkModalIsOpen(false)}
        footer={null}
      >
        <div
          style={{
            fontFamily: "Manrope, sans-serif",
            fontWeight: 800,
            fontSize: "18px",
            color: "#142C44",
            marginBottom: "12px",
          }}
        >
          Loan Remarks
        </div>
        {remarkDetails ? (
          <div>
            <div style={{ marginTop: "15px" }}>Type of Remark</div>
            <div style={{ width: "100%", marginTop: "5px" }}>
              <select
                style={{
                  width: "100%",
                  border: "1px solid lightgrey",
                  padding: "5px 10px",
                  borderRadius: "8px",
                }}
                value={remarkDetails.type}
                onChange={(event) => {
                  setRemarkDetails({
                    ...remarkDetails,
                    type: event.target.value,
                  });
                }}
              >
                <option value={"Good"}>Good</option>
                <option value={"Bad"}>Bad</option>
                <option value={""}>None</option>
              </select>
            </div>
            <div style={{ marginTop: "15px" }}>Remark message</div>
            <div style={{ width: "100%", marginTop: "5px" }}>
              <textarea
                value={remarkDetails.message}
                onChange={(event) => {
                  setRemarkDetails({
                    ...remarkDetails,
                    message: event.target.value,
                  });
                }}
                style={{
                  width: "100%",
                  border: "1px solid lightgrey",
                  padding: "5px 10px",
                  borderRadius: "8px",
                  height: "200px",
                }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() =>
                  editLoanRemarks(
                    remarkDetails.loan_id,
                    remarkDetails,
                    remarkDetails.loan_type || "corporate",
                  )
                }
                style={{
                  background: "rgb(17, 92, 166)",
                  color: "white",
                  padding: "7px 30px",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  marginTop: "10px",
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: 700,
                }}
              >
                Submit
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        width={"60%"}
        style={{ backgroundColor: "rgba(0, 0, 0, 0)" }}
        open={editBorrowerNotesModalIsOpen}
        onCancel={() => setEditBorrowerNotesModalIsOpen(false)}
        footer={null}
      >
        <div
          style={{
            fontFamily: "Manrope, sans-serif",
            fontWeight: 800,
            fontSize: "18px",
            color: "#142C44",
            marginBottom: "12px",
          }}
        >
          Loan Notes
        </div>
        <div>
          <div style={{ width: "100%", marginTop: "5px" }}>
            <textarea
              value={borrowerNotes}
              onChange={(event) => {
                setBorrowerNotes(event.target.value);
              }}
              placeholder="A text area for you to explain certain financial records or set notes for our platform administrators and lenders to view."
              style={{
                width: "100%",
                border: "1px solid lightgrey",
                padding: "5px 10px",
                borderRadius: "8px",
                height: "200px",
              }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => {
                if (selectedCorporateLoan?.__adminLoanId) {
                  editLoanNotes(
                    selectedCorporateLoan.__adminLoanId,
                    borrowerNotes,
                    selectedCorporateLoan.__loanType || "corporate",
                  );
                }
              }}
              style={{
                background: "rgb(17, 92, 166)",
                color: "white",
                padding: "7px 30px",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                marginTop: "10px",
                fontFamily: "Manrope, sans-serif",
                fontWeight: 700,
              }}
            >
              Submit
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AllLoans;
